import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Text from '../../atoms/Text';
import List from '../../molecules/List';
import Table from '../../molecules/Table';
import SortingWithText, { useSort } from '../../organisms/SortingWithText';
import { AccountListSortStandard } from '../../../types/sort';
import { FetchAccounts } from '../../../hooks/http/account/fetch-account';
import Button from '../../atoms/Button';
import Modal from '../../molecules/Modal';
import {
  CouponFeautreType,
  CouponSaleType
} from '@aimpact-korea/arrange-front-types';
import { MenuItem, Select } from '@material-ui/core';
import Flex from '../../molecules/Flex';
import { StoreCouponPresetList } from '../../../hooks/http/store-coupon-preset/store-coupon-preset';
import { IssueAccountCoupon } from '../../../hooks/http/account-coupon/issue-account-coupon';
import { useQueryClient } from '@tanstack/react-query';

export interface Props {
  keyword?: string;
}

const AccountsData: React.FC<Props> = ({ keyword }) => {
  const { props, sort } = useSort<AccountListSortStandard>({
    initState: {
      orderBy: 'DESC',
      standardColumn: 'createdAt'
    }
  });

  const query = FetchAccounts.useAccounts({
    keyword,
    cursor: {
      standardColumn: sort?.standardColumn,
      orderBy: sort?.orderBy
    }
  });

  return (
    <InfiniteScroll
      style={{ maxWidth: '2400px' }}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dataLength={query
        .data!.pages.map((page) => page.data.accounts.length)
        .reduce((sum, current) => sum + current, 0)}
      next={query.fetchNextPage}
      hasMore={query.hasNextPage ?? false}
      loader={<h4>Loading...</h4>}
    >
      <Table style={{ width: '100%' }}>
        <Table.Head>
          <Table.Row>
            <Table.Cell tag="th" alignType="center" className="col-1">
              유저 No.
              <br />
              코드
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              이름
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              번호
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              마지막 로그인 시간
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              <SortingWithText {...props('createdAt')}>
                가입날짜
              </SortingWithText>
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              쿠폰 발급
            </Table.Cell>
            <Table.Cell tag="th" alignType="center" className="col-1">
              보유 쿠폰 리스트
            </Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {query.data?.pages.map((group, i) => (
            <AccountsDataRow key={i} accounts={group.data.accounts} />
          ))}
        </Table.Body>
      </Table>
    </InfiniteScroll>
  );
};

export default AccountsData;

interface AccountsDataRowProps {
  accounts: FetchAccounts.AccountRes['accounts'];
}

const AccountsDataRow: React.FC<AccountsDataRowProps> = React.memo(
  ({ accounts }) => {
    const [isShowCouponIssueDialog, setIsShowCouponIssueDialog] =
      useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
      null
    );
    const [selectedCouponPresetId, setSelectedCouponPresetId] = useState<
      number | null
    >(null);

    const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);

    const queryClient = useQueryClient();

    const onClickShowCouponIssueDialog = (accountId: number) => {
      setSelectedAccountId(accountId);
      setIsShowCouponIssueDialog(true);
    };

    const handleCloseCouponIssueDialog = () => {
      setSelectedAccountId(null);
      setIsShowCouponIssueDialog(false);
      setSelectedCouponPresetId(null);
    };

    const issueStoreCouponMutate = IssueAccountCoupon.useMutate({
      onSuccess: () => {
        alert('쿠폰 발급이 완료 되었습니다.');
        handleCloseCouponIssueDialog();

        queryClient.refetchQueries([FetchAccounts.KEY_STRING], {
          stale: false
        });
      }
    });

    const storeCouponPresetListQuery = StoreCouponPresetList.useFetchQuery({
      // 광교 직거랩
      // 9051 - dev
      // 11890 - prod
      storeId: 11890,
      couponFeautreType: CouponFeautreType.PERSONAL
    });

    const getCouponPresetName = (
      couponPreset: StoreCouponPresetList.ResponseType['storeCouponPresets'][number]
    ) => {
      switch (couponPreset.couponPreset.couponSaleType) {
        case CouponSaleType.PRICE:
          return `${couponPreset.couponPreset.amount}원 할인 (최대 할인가: ${couponPreset.couponPreset.maxAmountPrice}, 최소 구매가: ${couponPreset.couponPreset.minAmountPrice})`;
        case CouponSaleType.PERCENT:
          return `${couponPreset.couponPreset.amount}% 할인 (최대 할인가: ${couponPreset.couponPreset.maxAmountPrice}, 최소 구매가: ${couponPreset.couponPreset.minAmountPrice})`;
      }
    };

    const issueStoreCoupon = (
      storeCouponPresetId: number,
      accountId: number,
      endDate: string
    ) => {
      issueStoreCouponMutate.mutate({
        accountId,
        endDate,
        storeCouponPresetId
      });
    };

    return (
      <React.Fragment>
        {accounts.map((account) => (
          <Table.Body.Row key={account.id}>
            <Table.Body.Row.Cell alignType="center">
              <List gap="large">
                <List.Item>
                  <Text size="small" isBold={true}>
                    No. {account.id}
                    <br />
                    {account.code}
                  </Text>
                </List.Item>
              </List>
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <Text>{account.name}</Text>
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <Text>{account.phone}</Text>
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <Text>{account.lastLoginedAt}</Text>
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <Text>{account.createdAt}</Text>
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <Button
                text="쿠폰 발급"
                onClick={() => onClickShowCouponIssueDialog(account.id)}
              />
            </Table.Body.Row.Cell>
            <Table.Body.Row.Cell alignType="center">
              <List>
                {account.coupons.map((coupon) => (
                  <List.Item key={coupon.id}>
                    <Text>
                      ({coupon.endDate}) {coupon.name}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </Table.Body.Row.Cell>
          </Table.Body.Row>
        ))}
        <Modal
          sizeType="large"
          isOpen={isShowCouponIssueDialog}
          handleClose={() => {
            setSelectedCouponPresetId(null);
            setIsShowCouponIssueDialog(false);
          }}
          isHandleEscClose={false}
          isHandleOutsideClickClose={false}
        >
          <Modal.Header>
            <Modal.Header.Title text="쿠폰 발급" />
          </Modal.Header>
          <Modal.Body>
            <Text>쿠폰 프리셋 선택</Text>
            <br />
            <Select
              onChange={(e) => {
                setSelectedCouponPresetId(Number(e.target.value));
              }}
              value={selectedCouponPresetId}
            >
              {storeCouponPresetListQuery.data?.data.storeCouponPresets.map(
                (storeCouponPreset) => (
                  <MenuItem
                    key={storeCouponPreset.id}
                    value={storeCouponPreset.id}
                  >
                    {getCouponPresetName(storeCouponPreset)}
                  </MenuItem>
                )
              )}
            </Select>
            <br />
            <br />
            <Text>쿠폰 종료일</Text>
            <br />
            <input
              type="date"
              onChange={(e) => {
                setSelectedEndDate(e.target.value);
              }}
              value={selectedEndDate ?? ''}
            />
          </Modal.Body>
          <Modal.Footer>
            <Flex
              style={{ width: '100%' }}
              direction="row"
              justifyContent="space-around"
            >
              <Button
                text="취소"
                onClick={() => {
                  handleCloseCouponIssueDialog();
                }}
              />
              <Button
                text="발급"
                onClick={() => {
                  issueStoreCoupon(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    selectedCouponPresetId!,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    selectedAccountId!,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    selectedEndDate!
                  );

                  handleCloseCouponIssueDialog();
                }}
              />
            </Flex>
          </Modal.Footer>
        </Modal>
      </React.Fragment>
    );
  }
);

AccountsDataRow.displayName = 'AccountsDataRow';
